/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { useText } from '@adobe-commerce/elsie/i18n';
import { classes } from '@adobe-commerce/elsie/lib';
import {
  FunctionComponent,
  HTMLAttributes,
  useCallback,
  useEffect,
} from 'preact/compat';
import { Button } from '../Button';
import { Close as CloseSVG } from '@adobe-commerce/elsie/icons';
import { VNode } from 'preact';
import { Portal } from '../Portal/Portal';

import '@adobe-commerce/elsie/components/Modal/Modal.css';

export interface ModalProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'size' | 'title'> {
  size?: 'small' | 'medium' | 'full';
  title?: VNode;
  centered?: boolean;
  onClose?: () => void;
  backgroundDim?: boolean;
  clickToDismiss?: boolean;
  escapeToDismiss?: boolean;
  showCloseButton?: boolean;
}

export const Modal: FunctionComponent<ModalProps> = ({
  size = 'small',
  title = null,
  centered = false,
  backgroundDim = true,
  clickToDismiss = true,
  escapeToDismiss = true,
  onClose,
  showCloseButton = true,
  className,
  children = null,
  ...props
}) => {
  const handleOnClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const translations = useText({
    modalCloseLabel: 'Dropin.Modal.Close.label',
  });

  // Close on background click
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const modal = document.querySelector('.dropin-modal');
      const modalBody = document.querySelector('.dropin-modal__body');

      if (
        clickToDismiss &&
        modal &&
        modalBody &&
        !modalBody.contains(event.target as Node)
      ) {
        handleOnClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [handleOnClose, clickToDismiss]);

  // Close on ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && escapeToDismiss) {
        handleOnClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleOnClose, escapeToDismiss]);

  // Prevent document scrolling
  useEffect(() => {
    const scrollingElement = document.scrollingElement as HTMLElement;

    const defaultOverflowValue = scrollingElement.style.overflow;

    scrollingElement.style.overflow = 'hidden';

    return () => {
      scrollingElement.style.overflow = defaultOverflowValue;
    };
  }, []);

  return (
    <Portal>
      <div
        className={classes([
          'dropin-modal',
          ['dropin-modal--dim', backgroundDim],
        ])}
      >
        <div
          {...props}
          className={classes([
            'dropin-modal__body',
            [`dropin-modal__body--${size}`, size],
            className,
          ])}
        >
          <div
            className={classes([
              'dropin-modal__header',
              ['dropin-modal__header-title', !!title],
            ])}
          >
            {title && (
              <div className={classes(['dropin-modal__header-title-content'])}>
                {title}
              </div>
            )}

            {showCloseButton && (
              <Button
                aria-label={translations.modalCloseLabel}
                variant="tertiary"
                className="dropin-modal__header-close-button"
                onClick={handleOnClose}
                icon={<CloseSVG />}
              />
            )}
          </div>

          <div
            className={classes([
              'dropin-modal__content',
              ['dropin-modal__body--centered', centered],
            ])}
          >
            {children}
          </div>
        </div>
      </div>
    </Portal>
  );
};
